import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, Car } from 'lucide-react';
import { visitStore } from '../../lib/visitStore';

/**
 * "My Visit" tray — the way back to the cars you already looked at.
 *
 * The real problem this solves: a shopper walks the lot, scans car 1, then 2,
 * then 3 — and cars 1 and 2 are gone, because browser back through a tapped NFC
 * card is not a thing a customer thinks about. So the tray is automatic, always
 * there, and collapsed until wanted.
 *
 * Shown only from the SECOND vehicle onwards: a tray listing the one car you are
 * already looking at is noise, not navigation.
 */
export default function MyVisitTray({ organizationId, currentCardId, onJump }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const vehicles = visitStore.list(organizationId);

    if (!organizationId || vehicles.length < 2) return null;

    const others = vehicles.filter((v) => v.card_id !== currentCardId);

    return (
        <div className="fixed left-3 right-3 bottom-3 z-40 pointer-events-none sm:left-auto sm:right-4 sm:w-[22rem]"
            data-testid="my-visit-tray">
            <div className="pointer-events-auto rounded-2xl border border-white/15 bg-slate-950/90 backdrop-blur-xl
                            shadow-[0_8px_40px_rgba(0,0,0,0.55)] overflow-hidden">
                <button type="button" onClick={() => setOpen((o) => !o)}
                    data-testid="my-visit-tray-toggle"
                    aria-expanded={open}
                    className="w-full flex items-center gap-2.5 px-4 min-h-[48px] text-left
                               hover:bg-white/[0.04] transition-colors">
                    <Car className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="flex-1 min-w-0 text-[13px] font-bold text-white">
                        {t('myVisit.title', 'My visit')}
                        <span className="ml-1.5 font-normal text-slate-400">
                            {t('myVisit.count', '{{count}} vehicles', { count: vehicles.length })}
                        </span>
                    </span>
                    {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                        : <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />}
                </button>

                {open && (
                    <div className="px-2 pb-2.5 pt-0.5 max-h-[45vh] overflow-y-auto" data-testid="my-visit-tray-list">
                        <p className="px-2 pb-2 text-[11px] text-slate-500 leading-relaxed">
                            {t('myVisit.hint', 'Every vehicle you have opened on this visit. Tap one to go back to it.')}
                        </p>
                        {others.length === 0 ? (
                            <p className="px-2 py-2 text-[13px] text-slate-500" data-testid="my-visit-tray-empty">
                                {t('myVisit.onlyThis', 'This is the only other vehicle so far.')}
                            </p>
                        ) : others.map((v) => (
                            <button key={v.card_id} type="button" onClick={() => onJump(v.card_id)}
                                data-testid={`my-visit-tray-item-${v.card_id}`}
                                className="w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left
                                           hover:bg-white/[0.06] active:bg-white/[0.1] transition-colors">
                                {v.photo ? (
                                    <img src={v.photo} alt="" loading="lazy"
                                        className="w-14 h-11 rounded-lg object-cover bg-slate-800 shrink-0" />
                                ) : (
                                    <span className="w-14 h-11 rounded-lg bg-slate-800 shrink-0 flex items-center justify-center">
                                        <Car className="w-4 h-4 text-slate-600" />
                                    </span>
                                )}
                                <span className="flex-1 min-w-0">
                                    <span className="block text-[13px] font-semibold text-slate-100 truncate">
                                        {v.label || v.card_id}
                                    </span>
                                    <span className="block text-[11px] text-slate-500 truncate">
                                        {v.price ? `$${Number(v.price).toLocaleString()}` : v.card_id}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
